// v2 cow — a full dairy build: a deep rounded barrel with a dewlap chest and
// raised croup, four big irregular hide patches draped over the body (mirrored
// between variants), sturdy legs with dark hooves joined at the hip, a lowered
// blocky head with a broad pink muzzle band, sideways droop ears, forward-curved
// horns, a pink udder between the hind legs and a rope tail with a dark tuft.
// Two hides: Holstein black-and-white and brown-and-white. Ported 1:1 from
// age-of-mcp's v2/cow.ts, using the library's shared `critter` (quadLegs) helper.
import * as THREE from "three";
import { defineModel, critter, type Part } from "../../lib";

const HIDES = [
  { hide: "#f0ebe0", shade: "#e4ddce", patch: "#2c2926", muzzle: "#d8a49c", horn: "#ddd2b4", leg: "#e6e0d2", hoof: "#352c22", udder: "#e8b2aa" },
  { hide: "#efe8da", shade: "#e2d9c6", patch: "#7a4a26", muzzle: "#d8a49c", horn: "#ddd2b4", leg: "#e0d6c4", hoof: "#352c22", udder: "#e8b2aa" },
];

function cowParts(v: number): Part[] {
  const c = HIDES[v % HIDES.length]!;
  const m = v % 2 === 0 ? 1 : -1; // mirror the patch layout between hides
  const parts: Part[] = critter.quadLegs({
    dx: 0.068, dz: 0.036, bodyY: 0.135, rTop: 0.0105, rFoot: 0.008,
    color: c.leg, hoof: { color: c.hoof, r: 0.0105, h: 0.016 },
  });
  parts.push(
    // deep barrel, dewlap chest, raised croup
    { geometry: new THREE.IcosahedronGeometry(0.064, 0), color: c.hide,
      position: [0, 0.138, 0], scale: [1.62, 1.0, 1.08] },
    { geometry: new THREE.IcosahedronGeometry(0.046, 0), color: c.shade,
      position: [0.062, 0.126, 0], scale: [1.0, 1.05, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.045, 0), color: c.hide,
      position: [-0.066, 0.146, 0], scale: [1.0, 0.95, 0.95] },
    // four irregular hide patches draped over the body
    { geometry: new THREE.IcosahedronGeometry(0.03, 0), color: c.patch,
      position: [0.028, 0.168, 0.042 * m], scale: [1.5, 0.75, 0.7] },
    { geometry: new THREE.IcosahedronGeometry(0.028, 0), color: c.patch,
      position: [-0.05, 0.135, -0.046 * m], scale: [1.4, 0.85, 0.65] },
    { geometry: new THREE.IcosahedronGeometry(0.022, 0), color: c.patch,
      position: [-0.018, 0.114, 0.05 * m], scale: [1.2, 0.8, 0.6] },
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: c.patch,
      position: [-0.078, 0.172, 0.012 * m], scale: [1.2, 0.6, 1.0] },
    // pink udder between the hind legs
    { geometry: new THREE.IcosahedronGeometry(0.021, 0), color: c.udder,
      position: [-0.045, 0.088, 0], scale: [1.15, 0.8, 1.05] },
    // short lowered neck into the blocky head
    { geometry: new THREE.CylinderGeometry(0.022, 0.028, 0.05, 5), color: c.hide,
      position: [0.103, 0.148, 0], rotation: [0, 0, -0.8] },
    { geometry: new THREE.BoxGeometry(0.05, 0.042, 0.046), color: c.hide,
      position: [0.138, 0.158, 0] },
    // broad pink muzzle band across the front
    { geometry: new THREE.CylinderGeometry(0.0165, 0.0165, 0.026, 6), color: c.muzzle,
      position: [0.168, 0.147, 0], rotation: [0, 0, Math.PI / 2] },
    // sideways droop ears
    { geometry: new THREE.IcosahedronGeometry(0.0105, 0), color: c.shade,
      position: [0.128, 0.176, 0.032], scale: [1.0, 0.5, 1.7], rotation: [0.4, 0, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.0105, 0), color: c.shade,
      position: [0.128, 0.176, -0.032], scale: [1.0, 0.5, 1.7], rotation: [-0.4, 0, 0] },
    // forward-curved horns — tilted up, out and a touch ahead
    { geometry: new THREE.ConeGeometry(0.006, 0.027, 4), color: c.horn,
      position: [0.128, 0.192, 0.021], rotation: [0, -1.107, -0.73] },
    { geometry: new THREE.ConeGeometry(0.006, 0.027, 4), color: c.horn,
      position: [0.128, 0.192, -0.021], rotation: [0, 1.107, -0.73] },
    // eyes
    { geometry: new THREE.IcosahedronGeometry(0.0036, 0), color: "#241c14",
      position: [0.148, 0.166, 0.0245] },
    { geometry: new THREE.IcosahedronGeometry(0.0036, 0), color: "#241c14",
      position: [0.148, 0.166, -0.0245] },
    // rope tail hanging down-back, dark tuft at the tip
    { geometry: new THREE.CylinderGeometry(0.005, 0.007, 0.075, 4), color: c.hide,
      position: [-0.128, 0.144, 0], rotation: [0, 0, 2.4] },
    { geometry: new THREE.IcosahedronGeometry(0.0095, 0), color: c.patch,
      position: [-0.152, 0.112, 0] },
  );
  return parts;
}

export const cowV2 = defineModel({
  id: "cow-v2",
  scale: 2.75, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Cow (v2)",
  tags: ["creature","cow","low-poly","age-of-mcp","fable"],
  summary: "Low-poly cow.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.16,
  fits: ["draft"],
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => cowParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => cowParts(1) },
  ],
  animations: [],
});
